import { useRef, useEffect, useCallback } from 'react'
import throttle from 'lodash/throttle'

type Axis = 'x' | 'y';

const windowScrollPositionKey: any = {
  y: 'pageYOffset',
  x: 'pageXOffset'
}

const documentScrollPositionKey: any = {
  y: 'scrollTop',
  x: 'scrollLeft'
}

const getScrollPosition = (axis: Axis) => {
  return (window[windowScrollPositionKey[axis]] ||
  (document.documentElement as any)[documentScrollPositionKey[axis]] ||
  (document.body as any)[documentScrollPositionKey[axis]] ||
  0) as any;
}
  

interface Props {
  children: React.FC<any>;
  throttleTime?: number;
  isGrid?: boolean;
}

export const ReactWindowScroller: React.FC<Props> = ({
  children,
  throttleTime = 10,
  isGrid = false
}) => {
  const ref = useRef();
  const outerRef = useRef() as any;

  useEffect(() => {
    const handleWindowScroll = throttle(() => {
      const { offsetTop = 0, offsetLeft = 0 } = outerRef.current || {}
      const scrollTop = getScrollPosition('y') - offsetTop
      const scrollLeft = getScrollPosition('x') - offsetLeft
      if (isGrid) ref.current && (ref.current as any).scrollTo({ scrollLeft, scrollTop })
      if (!isGrid) ref.current && (ref.current as any).scrollTo(scrollTop)
    }, throttleTime)

    window.addEventListener('scroll', handleWindowScroll)
    return () => {
      handleWindowScroll.cancel()
      window.removeEventListener('scroll', handleWindowScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrid])

  const onScroll = useCallback(
    ({ scrollLeft, scrollTop, scrollOffset, scrollUpdateWasRequested }) => {
      if (!scrollUpdateWasRequested) return
      const top = getScrollPosition('y')
      const left = getScrollPosition('x')
      const { offsetTop = 0, offsetLeft = 0 } = outerRef.current || {}

      scrollOffset += Math.min(top, offsetTop)
      scrollTop += Math.min(top, offsetTop)
      scrollLeft += Math.min(left, offsetLeft)

      if (!isGrid && scrollOffset !== top) window.scrollTo(0, scrollOffset)
      if (isGrid && (scrollTop !== top || scrollLeft !== left)) {
        window.scrollTo(scrollLeft, scrollTop)
      }
    },
    [isGrid]
  )

  return children({
    ref,
    outerRef,
    style: {
      width: isGrid ? 'auto' : '100%',
      height: '100%',
      display: 'inline-block'
    },
    onScroll
  })
}